import { useEffect } from 'react';

/**
 * Hook to apply company theme settings to CSS variables
 * @param {Object} customization - Company customization settings
 */
export const useTheme = (customization) => {
  useEffect(() => {
    if (!customization) return;

    const root = document.documentElement;

    // Apply primary color
    if (customization.primaryColor) {
      root.style.setProperty('--primary-color', customization.primaryColor);

      // Calculate hover color (darker shade)
      const primaryHover = adjustColor(customization.primaryColor, -20);
      root.style.setProperty('--primary-hover', primaryHover);
    }

    // Apply secondary color
    if (customization.secondaryColor) {
      root.style.setProperty('--secondary-color', customization.secondaryColor);

      // Calculate hover color (darker shade)
      const secondaryHover = adjustColor(customization.secondaryColor, -20);
      root.style.setProperty('--secondary-hover', secondaryHover);
    }

    // Apply theme class to body if companyTheme is specified
    if (customization.companyTheme && customization.companyTheme !== 'default') {
      document.body.className = document.body.className.replace(/theme-\w+/g, '');
      document.body.classList.add(`theme-${customization.companyTheme}`);
    } else {
      // Remove theme classes if default
      document.body.className = document.body.className.replace(/theme-\w+/g, '');
      // Reset to default theme variables
      root.style.setProperty('--bg-primary', '#ffffff');
      root.style.setProperty('--bg-secondary', '#f9fafb');
      root.style.setProperty('--bg-tertiary', '#f3f4f6');
      root.style.setProperty('--text-primary', '#111827');
      root.style.setProperty('--text-secondary', '#6b7280');
      root.style.setProperty('--text-muted', '#9ca3af');
      root.style.setProperty('--border-color', '#e5e7eb');
      root.style.setProperty('--shadow', '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)');
    }
  }, [customization]);
};

/**
 * Helper function to adjust color brightness
 * @param {string} color - Hex color code
 * @param {number} amount - Amount to adjust (-255 to 255)
 * @returns {string} Adjusted hex color
 */
const adjustColor = (color, amount) => {
  // Remove # if present
  color = color.replace(/^#/, '');

  // Parse r, g, b values
  const num = parseInt(color, 16);
  let r = (num >> 16) + amount;
  let g = (num >> 8 & 0x00FF) + amount;
  let b = (num & 0x0000FF) + amount;

  // Ensure values stay within 0-255
  r = r > 255 ? 255 : r < 0 ? 0 : r;
  g = g > 255 ? 255 : g < 0 ? 0 : g;
  b = b > 255 ? 255 : b < 0 ? 0 : b;

  // Convert back to hex
  return '#' + (0x1000000 + (r << 16) + (g << 8) + b).toString(16).slice(1);
};